import { useCallback, useMemo, useEffect } from 'react';

import Logger from '@/utils/CapcitorLog';
import { YG_DEV_MISSING_SENSORS } from '@/utils/YG_DEV_MISSING_SENSORS';

import { hubSensorService, sensorService } from '@/controllers/sensorService';

import { useAnnotationCounts } from '@/contexts/AnnotationCountsContext';
import { useSensorsContext } from '@/contexts/SensorsContext';

import { KinematicModelOptions, Assignments } from '@/plugins/NativeQSenseControl';

import { MovementType } from '@/types/MovementTypes';

interface UseSensorsHook {
  searchAndConnect: (allowedDevices: string[]) => Promise<void>;
  startCalibration: () => Promise<void>;
  stopMeasurement: VoidFunction;
  startMeasurement: (movementType: MovementType) => void;
  disconnectAll: () => Promise<void>;
  deleteAnnotation: (count: number, movementType: MovementType) => void;
  restoreAnnotation: (count: number, movementType: MovementType) => void;
  resetDeletedAnnotations: VoidFunction;
  updateTag: (count: number, movementType: MovementType, tagId: string | undefined) => void;
  resetTags: VoidFunction;
  resetSession: VoidFunction;
  init: (
    sessionId: string,
    sessionStartTime: Date,
    config: {
      KinematicModel: KinematicModelOptions;
      Whitelist: string[];
      Assignments: Assignments[];
      NetworkKey: number;
      DeviceState: string;
    },
    clientName: string,
    userId: string,
    clientId: string,
    idToken: string,
  ) => void;
}

export function useSensors(isHubFlow: boolean): UseSensorsHook {
  const { state, increase } = useAnnotationCounts();
  const {
    startCalibration: startCalibrationState,
    finishCalibration: finishCalibrationState,
    setAllConnected,
    sensorConnected,
    reset,
    setRecording,
    state: sensorsState,
    setSessionStarted,
  } = useSensorsContext();

  const service = useMemo(() => (isHubFlow ? hubSensorService : sensorService), [isHubFlow]);

  const searchAndConnect = useCallback(async (allowedDevices: string[]) => {
    // If all sensors are already connected, do nothing
    const sensorsThatNeedToBeConnected = sensorsState.sensorsInDatabase
      .filter((sensor) => sensor.id !== 'unknown')
      .map((sensor) => sensor.id);

    if (sensorsThatNeedToBeConnected.every((sensor) => sensorsState.connectedSensors.includes(sensor))) {
      return;
    }

    setAllConnected(false);
    await sensorService.disconnectDevices();
    await sensorService.startScan(allowedDevices);

    YG_DEV_MISSING_SENSORS.forEach((macAddress) => {
      if (allowedDevices.includes(macAddress)) {
        sensorService.emit('deviceConnected', { macAddress });
        sensorService.emit('batteryUpdate', { macAddress, battery: 65 });
        sensorConnected(macAddress);
      }
    });
  }, []);

  const disconnectAll = useCallback(async () => {
    reset();

    await service.stopMeasurement();

    if (isHubFlow) {
      await hubSensorService.endSession();

      await new Promise((resolve) => setTimeout(resolve, 5000));
    }

    await service.disconnectDevices();
  }, [isHubFlow, service, reset]);

  const startCalibration = useCallback(async () => {
    console.log('Starting calibration for all sensors (useSensors)');
    // Emit that calibration has started
    sensorService.emit('calibrationStarted');

    // Compute the current count and derive new count, we do this instead of increase
    const previousCount = state.get(MovementType.calibration) ?? 0;
    const newCount = previousCount + 1;

    // Dispatch the increase action to update the state
    increase(MovementType.calibration);
    startCalibrationState();

    // Start the calibration
    service.startCalibration(newCount, finishCalibrationState);
  }, [state, service]);

  const startMeasurement = useCallback(
    (movementType: MovementType) => {
      // Compute the current count and derive new count, we do this instead of increase
      const previousCount = state.get(movementType) ?? 0;
      const newCount = previousCount + 1;

      // Dispatch the increase action to update the state
      increase(movementType);

      setRecording(true);

      // Start the measurement
      service.startMeasurement(movementType, newCount);
    },
    [state, service],
  );

  const stopMeasurement = useCallback(() => {
    service.stopMeasurement();
    setRecording(false);
  }, [service]);

  const deleteAnnotation = useCallback(
    (count: number, movementType: MovementType) => {
      service.deleteAnnotation(count, movementType);
    },
    [service],
  );

  const restoreAnnotation = useCallback(
    (count: number, movementType: MovementType) => {
      service.restoreAnnotation(count, movementType);
    },
    [service],
  );

  const resetDeletedAnnotations = useCallback(() => {
    service.resetDeletedAnnotations();
  }, [service]);

  const updateTag = useCallback(
    (count: number, movementType: MovementType, tagId: string | undefined) => {
      // If no tag is provided, delete the tag
      if (!tagId) {
        service.deleteTag(movementType, count);
        return;
      }

      // Update the tag in the swift layer
      service.updateTag(movementType, count, tagId);
    },
    [service],
  );

  const resetTags = useCallback(() => {
    service.resetTags();
  }, [service]);

  const resetSession = useCallback(() => {
    service.endSession();
  }, [service]);

  const init = useCallback(
    (
      sessionId: string,
      sessionStartTime: Date,
      config: {
        KinematicModel: KinematicModelOptions;
        Whitelist: string[];
        Assignments: Assignments[];
        NetworkKey: number;
        DeviceState: string;
      },
      clientName: string,
      userId: string,
      idToken: string,
      clientId: string,
    ) => {
      service.initialize(sessionId, sessionStartTime, config, clientName, userId, clientId, idToken);
    },
    [service],
  );

  useEffect(() => {
    if (sensorsState.sensorsInDatabase.length === 0) {
      return;
    }

    const sensorsThatNeedToBeConnected = sensorsState.sensorsInDatabase
      .filter((sensor) => sensor.id !== 'unknown')
      .map((sensor) => sensor.id);

    // Check if connected sensors contains all the sensors that need to be connected
    if (sensorsThatNeedToBeConnected.every((sensor) => sensorsState.connectedSensors.includes(sensor))) {
      setAllConnected(true);

      if (!sensorsState.sessionStarted) {
        Logger.info('Starting session for the first time');
        sensorService.stopScan();
        setTimeout(() => {
          service.startSession();
          setSessionStarted(true); // Mark session as started
        }, 300);
      }
    } else {
      setAllConnected(false);
    }
  }, [sensorsState.connectedSensors, sensorsState.sensorsInDatabase, sensorsState.sessionStarted, service]);

  return {
    searchAndConnect,
    startCalibration,
    stopMeasurement,
    startMeasurement,
    disconnectAll,
    deleteAnnotation,
    restoreAnnotation,
    resetDeletedAnnotations,
    updateTag,
    resetTags,
    resetSession,
    init,
  };
}
